import React, { FC, Ref, useCallback, useMemo, useState, forwardRef, useImperativeHandle, ReactNode } from "react";
import { makeStyles, PopoverOrigin } from "@material-ui/core";
import { ClassNameMap } from "@material-ui/styles";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
import { MORE_MENU_COL_WIDTH } from "constants/sizingView";
import { DATATEST } from "constants/integration";
import clsx from "classnames";

const defaultAnchorOrigin: PopoverOrigin = {
    vertical: "bottom",
    horizontal: "left",
};
const defaultTransformOrigin: PopoverOrigin = {
    vertical: "top",
    horizontal: "right",
};
interface Props {
    children: ReactNode;
    classes?: {
        button?: Partial<ClassNameMap>;
        menu?: Partial<ClassNameMap>;
    };
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    closeOnClick?: boolean;
}

interface MoreMenuHandle {
    onClose: () => void;
}

const MoreMenu: FC<Props> = (props, ref: Ref<MoreMenuHandle>) => {
    const { children, closeOnClick, classes, anchorOrigin, transformOrigin } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClose = useCallback((event?: any) => {
        setAnchorEl(null);
        event?.stopPropagation?.();
        event?.preventDefault?.();
    }, []);
    const handleOpen = useCallback(
        (event) => {
            event.stopPropagation();
            event.preventDefault();
            !anchorEl && setAnchorEl(event.currentTarget);
        },
        [anchorEl]
    );

    const initClasses = useStyles();

    const menuClasses = useMemo(() => ({ paper: initClasses.popover }), [initClasses]);
    const buttonClasses = useMemo(() => ({ root: initClasses.button }), [initClasses]);

    useImperativeHandle(ref, () => ({ onClose: handleClose }), [handleClose]);

    return (
        <div className={clsx("more-menu", { active: !!anchorEl })}>
            <IconButton
                onClick={handleOpen}
                classes={classes?.button || buttonClasses}
                data-test={DATATEST.GRID_VIEW.MORE_MENU.MENU_BUTTON}
                disableFocusRipple
            >
                <MoreVert fontSize="large" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleClose}
                onClick={closeOnClick && handleClose}
                classes={classes?.menu || menuClasses}
                anchorOrigin={anchorOrigin || defaultAnchorOrigin}
                transformOrigin={transformOrigin || defaultTransformOrigin}
                getContentAnchorEl={null}
            >
                {children}
            </Menu>
        </div>
    );
};

export default forwardRef<MoreMenuHandle, Props>(MoreMenu);

const useStyles = makeStyles({
    popover: {
        minWidth: "200px",
        "& .MuiButtonBase-root": {
            fontSize: "1.3rem",
        },
    },
    button: {
        padding: 0,
        width: MORE_MENU_COL_WIDTH,
    },
});
